/**
 * fix/bylayer-colour-never-resolves-on-canvas — `adapterCtxBase` builder
 * extracted from `SceneContent.tsx` so the wiring that turns the live
 * `styleTables.layers` table into the adapter's `AdapterContext` is a
 * plain, importable, unit-testable function instead of an inline
 * `useMemo` closure only reachable by mounting the full `<Canvas>` /
 * `useThree` R3F tree.
 *
 * This is the EXACT code `SceneContent`'s `useMemo` calls (see its
 * `adapterCtxBase` definition) — testing this function with a realistic
 * `styleTables.layers`-shaped input (numeric ACI `color`, the actual
 * runtime shape) is a wiring-level proof, not an adapter-level one: the
 * bug this PR fixes was `SceneContent` handing the adapter an EMPTY
 * `buildLayerColorMap([])` literal regardless of what the layer table
 * contained — the adapter itself (`resolveColorHex` / `buildLayerColorMap`)
 * was always correct and already covered by
 * `adapters/__tests__/lineToRenderable.test.ts`.
 */

import type { DimensionStyle } from "@dxf-ai-editor/drawing-model";

import type { Scene3DStageProps } from "../Scene3DStageProps";
import { type AdapterContext } from "../adapters";
import { activeAnnotationScaleForSpace } from "../adapters/typed/annotativeResolve";
import { buildLayerColorMapFromLayerDefinitions } from "../rendering/colorResolution";
import { buildLayerLinetypeMap } from "../rendering/linetypeResolve";

/**
 * Minimal layer-table row shape this builder needs. `SceneContent` passes
 * its `layerStates` (subscribed as the narrower `LayerVisibilityState[]`
 * shape but carrying the full `LayerDefinition` at runtime — see the FIX-2
 * comment on `SceneContent`'s `layerStates` subscription) through this
 * structural type so this module stays free of a `drawing-model` import
 * for the layer-table shape itself (matches `layerVisibility.ts`'s own
 * "don't pull the whole package in for a narrow read" precedent).
 */
export interface AdapterCtxLayerRow {
  name: string;
  /** ACI colour index (numeric) — `LayerDefinition.color`. */
  color?: number;
  linetype?: string;
}

export interface BuildAdapterCtxBaseArgs {
  /** `styleTables.layers` (or the narrowed subscription of it). */
  layerStates: ReadonlyArray<AdapterCtxLayerRow>;
  /** Lane H / S8 — DIMSTYLE table, name-keyed. */
  dimensionStylesByName: ReadonlyMap<string, DimensionStyle>;
  blockDefinitions: Scene3DStageProps["blockDefinitions"];
  /** Lane H / S9 — active annotation scale FACTOR (CANNOSCALE → numeric). */
  resolvedAnnotationScale: number | null;
  cannoscaleName: Scene3DStageProps["cannoscaleName"];
  /**
   * `DrawingDocument.activeSpaceId` — the space the user is IN. REQUIRED (not
   * defaulted): a pass that renders a layout also carries model space, and
   * telling the two apart is what keeps model geometry white on the dark
   * viewport background while the sheet's own ink goes black
   * (`rendering/layoutForeground.ts`). A caller that cannot say which space is
   * active cannot make that distinction, so it must say so explicitly rather
   * than inherit a silent default.
   */
  activeSpaceId: string;
  activeSpaceKind: string;
  annoAllVisible: Scene3DStageProps["annoAllVisible"];
  /**
   * The drawing's `$FILLMODE` HEADER variable. Passed STRAIGHT THROUGH (no
   * `?? 1` default here): the absent-means-1 rule lives in ONE place, the
   * `fillModePaintsSolid` reader in `adapters/geom/polylineDrawnBand.ts`, and
   * the shell stamps the field only for the non-default 0 so a default drawing
   * keeps byte-identical renderables.
   */
  fillmode?: Scene3DStageProps["fillmode"];
}

/**
 * Build the shared adapter context base every entity in the active space
 * renders through.
 *
 * # AutoCAD parity — layer table wiring
 *
 * `layerColors` / `layerLinetypes` are BOTH derived from the SAME
 * `layerStates` table (`styleTables.layers`) — a BYLAYER entity's colour
 * AND its linetype resolve off the identical layer row. Before this fix
 * `layerColors` was hardcoded to `buildLayerColorMap([])` (PR-I left it
 * inert pending this wiring), so `resolveColorHex`'s BYLAYER branch always
 * missed the lookup and fell back to `FALLBACK_HEX` (`#ffffff`) — every
 * drawing rendered monochrome white regardless of its layer colours.
 *
 * `buildLayerColorMapFromLayerDefinitions` performs the numeric-ACI → hex
 * conversion; skipping the numeric→string bridge (or reusing
 * `buildLayerColorMap` directly on `LayerDefinition[]`, whose `color` is a
 * `number` where `LayerColorRecord.color` needs a `string`) reproduces the
 * exact same silent-empty-lookup bug with more code — see that function's
 * doc comment.
 *
 * # SCOPED LIMITATION (@codex round-20/22, prior `SceneContent.tsx:271` —
 * "Use viewport CANNOSCALE for viewport passes")
 *
 * A paper layout's model VIEWPORTs can each carry their own
 * `viewportCannoscaleName` / `viewportAnnoAllVisible` (model.ts:1526/1528),
 * but `rendering/viewportRenderPass.ts` renders the ONE shared `modelScene`
 * (built from this single context) through every VP's camera + a per-VP
 * visibility MASK — the mask can hide/show objects but cannot RE-TRANSFORM
 * annotative geometry per VP. Faithful per-VP annotative representation
 * therefore needs a per-viewport model render (a separate MC-series slice
 * in the still-evolving viewport-render subsystem), NOT reachable from this
 * shared-scene seam. Until then a layout's model-VP content inherits this
 * space-level decision.
 */
export function buildAdapterCtxBase(
  args: BuildAdapterCtxBaseArgs,
): AdapterContext {
  const {
    layerStates,
    dimensionStylesByName,
    blockDefinitions,
    resolvedAnnotationScale,
    cannoscaleName,
    activeSpaceId,
    activeSpaceKind,
    annoAllVisible,
    fillmode,
  } = args;
  return {
    layerColors: buildLayerColorMapFromLayerDefinitions(layerStates),
    layerLinetypes: buildLayerLinetypeMap(layerStates),
    cameraZoom: 1,
    zOrderIndex: 0,
    dimensionStyles: dimensionStylesByName,
    blocks: blockDefinitions ?? {},
    annotationScale: resolvedAnnotationScale,
    // @codex round-22 — resolve the active annotation scale against the
    // ACTIVE SPACE, not unconditionally against the document header
    // CANNOSCALE (see `activeAnnotationScaleForSpace`'s own doc for the
    // model-space-vs-layout split).
    activeAnnotationScale: activeAnnotationScaleForSpace(
      cannoscaleName,
      activeSpaceKind,
    ),
    annoAllVisible: annoAllVisible ?? true,
    fillmode,
    // AutoCAD colour 7 is "black/white" — the contrast of the drawing-area
    // background, not a palette entry. The context carries WHICH SPACE this
    // pass is in, never a ready-made foreground: a layout pass renders the
    // white sheet AND (through its viewports, on the dark model background)
    // model space, so the ink is a PER-ENTITY answer derived from the entity's
    // own space. `adapters/adapterContext.ts entityForegroundHex` asks, and
    // `rendering/layoutForeground.ts` owns the (background, foreground) pair,
    // the ezdxf reference and both measured bugs.
    activeSpaceId,
    activeSpaceKind,
  };
}
